import React, { useState } from 'react';
import { Check, Shield, Lock, Crown, X, Sparkles, CreditCard, Zap } from 'lucide-react';
import { logEvent, EVENTS } from '../../services/eventLogger';

export default function PaywallModal({ feature = 'general', inline = false, onClose }) {
  const [billingCycle, setBillingCycle] = useState('annual');
  const [isProcessing, setIsProcessing] = useState(false);

  React.useEffect(() => {
    logEvent(EVENTS.PAYWALL_VIEWED, { feature });
  }, [feature]);

  // Pro features — key/limited-on-free items highlighted at the top, then bonus
  // tools. Free-tier numbers mirror LIMITS in src/contexts/PlanContext.js.
  const features = [
    { text: 'เพิ่มหลักทรัพย์ในพอร์ตไม่จำกัด', sub: 'ฟรี: 5 ตัว', highlight: true },
    { text: 'สร้าง Watchlist ได้หลายกลุ่ม', sub: 'ฟรี: 1 กลุ่ม', highlight: true },
    { text: 'ตั้งแนวรับ + แจ้งเตือนผ่าน Telegram ไม่จำกัดหุ้น', sub: 'ฟรี: 3 หุ้น', highlight: true },
    { text: 'วิเคราะห์หุ้นเชิงลึก — เทคนิคอล/งบการเงิน/นักวิเคราะห์/ข่าว', sub: 'ฟรี: ดูกราฟพื้นฐาน', highlight: true },
    { text: 'เตือนเมื่อถือหุ้นเกินเพดาน % ที่ตั้งไว้', sub: null, highlight: true },
    { text: 'จำลองแผน DCA & ดอกเบี้ยทบต้นไม่จำกัด', sub: null, highlight: false },
    { text: 'เครื่องคำนวณเงินปันผล + ปฏิทินปันผล/ประกาศงบ', sub: null, highlight: false },
    { text: 'เครื่องมือจัดสรรพอร์ต (Allocator)', sub: null, highlight: false },
    { text: 'วิเคราะห์พอร์ตเชิงลึก + กราฟสัดส่วน', sub: null, highlight: false },
    { text: 'รายงานภาษี FIFO + ส่งออก CSV', sub: null, highlight: false },
  ];

  const prices = {
    monthly: { amount: 69, label: '/เดือน', perDay: '~฿2.3/วัน' },
    annual: { amount: 690, label: '/ปี', perDay: '~฿1.9/วัน', save: 'ประหยัด 17%' },
  };
  const selectedPrice = prices[billingCycle];

  const handleUpgrade = async () => {
    try {
      setIsProcessing(true);
      logEvent(EVENTS.UPGRADE_CLICKED, { feature, cycle: billingCycle });
      
      const { auth } = require('../../firebase');
      const token = await auth.currentUser.getIdToken();
      
      const response = await fetch('https://us-central1-investport-pro.cloudfunctions.net/createStripeCheckoutSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cycle: billingCycle })
      });
      
      const session = await response.json();
      if (session.error) {
        throw new Error(session.error);
      }
      
      // Redirect to Stripe Checkout
      window.location.href = session.url;
      
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsProcessing(false);
    }
  };

  const content = (
    <div className={`w-full max-w-md mx-auto bg-white dark:bg-slate-900 shadow-2xl rounded-2xl border border-slate-200 dark:border-slate-700/60 flex flex-col ${inline ? 'max-h-full' : 'max-h-[90vh]'}`}>
      
      {/* Header Banner */}
      <div className="relative px-4 py-4 sm:p-6 text-center shrink-0 rounded-t-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 overflow-hidden">
        {/* Close button */}
        {onClose && !inline && (
          <button onClick={onClose} className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10">
            <X size={14} className="text-white/80" />
          </button>
        )}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/5 to-transparent pointer-events-none"></div>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-[10px] font-bold mb-2 border border-amber-400/20">
            <Sparkles size={9} /> ทดลองใช้ฟรี 14 วัน
          </div>
          <h2 className="text-lg sm:text-2xl font-black text-white tracking-tight leading-tight">
            อัปเกรดเป็น Pro
          </h2>
          <p className="text-indigo-100 text-[11px] sm:text-sm mt-1 opacity-90">
            ปลดล็อกเครื่องมือทั้งหมด วิเคราะห์พอร์ตแบบมืออาชีพ
          </p>
        </div>
      </div>

      <div className="px-4 py-3 sm:p-6 overflow-y-auto flex-1">
        
        {/* Billing Toggle */}
        <div className="flex bg-slate-100 dark:bg-slate-800/60 p-1 rounded-xl mb-3 sm:mb-5 ring-1 ring-inset ring-slate-200/50 dark:ring-slate-700/50">
          <button 
            type="button"
            onClick={() => setBillingCycle('monthly')}
            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all duration-200 ${billingCycle === 'monthly' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
          >
            รายเดือน
          </button>
          <button 
            type="button"
            onClick={() => setBillingCycle('annual')}
            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 ${billingCycle === 'annual' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
          >
            รายปี <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${billingCycle === 'annual' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'}`}>-17%</span>
          </button>
        </div>

        {/* Price — subtle inline */}
        <div className="flex items-center justify-between mb-3 sm:mb-4 px-0.5">
          <div className="flex items-baseline gap-1">
            <span className="text-base font-bold text-slate-600 dark:text-slate-300">
              ฿{selectedPrice.amount}
            </span>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {selectedPrice.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400 dark:text-slate-500">{selectedPrice.perDay}</span>
            {billingCycle === 'annual' && (
              <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400">{selectedPrice.save}</span>
            )}
          </div>
        </div>


        {/* Feature Checklist — compact */}
        <div className="mb-4 sm:mb-5">
          <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">ฟีเจอร์ Pro ทั้งหมด</p>
          <div className="space-y-1.5">
            {features.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <div className={`rounded-full flex items-center justify-center shrink-0 ${item.highlight ? 'text-indigo-500 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`} style={{ width: 16, height: 16 }}>
                  <Check size={10} strokeWidth={3} />
                </div>
                <div className="flex-1 min-w-0 flex items-baseline gap-1.5">
                  <p className={`text-[12px] leading-tight ${item.highlight ? 'font-semibold text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
                    {item.text}
                  </p>
                  {item.sub && (
                    <p className="text-[9px] text-slate-400 dark:text-slate-500 whitespace-nowrap">({item.sub})</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <button 
          disabled={isProcessing}
          onClick={handleUpgrade}
          className="w-full py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 focus:ring-4 focus:ring-indigo-600/20 transition-all flex justify-center items-center gap-2 group disabled:opacity-70 shadow-lg shadow-indigo-600/20"
        >
          {isProcessing ? (
            <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> กำลังดำเนินการ...</span>
          ) : (
            <>
              <Zap size={16} className="opacity-80" />
              เริ่มทดลองใช้ Pro ฟรี 14 วัน
            </>
          )}
        </button>
        
        {onClose && (
          <button 
            onClick={onClose}
            className="w-full py-2.5 mt-2 text-xs font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
          >
            ไว้ทีหลัง
          </button>
        )}

        {/* Footer Trust Signals — single line */}
        <div className="mt-3 flex items-center justify-center gap-3 text-[9px] text-slate-400 dark:text-slate-500">
          <span className="flex items-center gap-1"><CreditCard size={9} /> Stripe</span>
          <span className="flex items-center gap-1"><Shield size={9} /> SSL</span>
          <span>ต้องผูกบัตร · ยกเลิกได้ตลอด</span>
        </div>
      </div>
    </div>
  );

  // If used inside a modal popup
  if (!inline) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 dark:bg-black/30 backdrop-blur-xl animate-in fade-in" onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
        {content}
      </div>
    );
  }

  // If used directly (e.g. injected under a blurred ProGate)
  return content;
}
